import { Injectable, inject } from '@angular/core';
import { AnalyticsService } from '../analytics/analytics.service';
import { getAuth, signOut, signInWithCustomToken } from 'firebase/auth';
import { EventName } from '../analytics/event-names';
import { SignOutService } from '../sign-out/sign-out.service';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/core/services/profile/profile.service';
import { OperatorService } from 'src/app/core/services/operator/operator.service';
import { JobService } from '../job/job.service';
import { Job } from '../job/job';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private profileService = inject(ProfileService);
  private analyticsService = inject(AnalyticsService);
  private signOutService = inject(SignOutService);
  private operatorService = inject(OperatorService);
  private jobService = inject(JobService);

  isAuthenticated(): Job<boolean> {
    return this.jobService.create<boolean>(async () => {
      if (OperatorService.IsDemo) {
        return true;
      }
      const auth = getAuth();
      const user = auth.currentUser;
      return user !== null;
    });
  }

  signInWithToken(token: string): Job<void> {
    return this.jobService.create(async () => {
      if (OperatorService.IsDemo) {
        return;
      }

      const auth = getAuth();
      await this.withFirebaseGuard(
        () => signInWithCustomToken(auth, token),
        'Auth signInWithToken',
      );
    });
  }

  async signOut(): Promise<void> {
    await this.signOutService.signOut();

    if (!OperatorService.IsDemo) {
      const auth = getAuth();
      await this.withFirebaseGuard(() => signOut(auth), 'Auth signOut');
    }
  }

  loadCurrentUser(): Job<boolean> {
    return this.jobService.create(async () => {
      if (OperatorService.IsDemo) {
        this.setUser('demo');
        return true;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        await this.setUser(user.uid);
        return true;
      }
      return false;
    });
  }

  private async setUser(userId: string) {
    const version = environment.version;
    if (version) {
      this.analyticsService.setProperty('appVersion', version);
    }
    this.analyticsService.setProperty('build', environment.build.toString());
    this.analyticsService.setProperty('bundleId', 'gg.clinch.embed');
    this.analyticsService.log(EventName.OperatorInitialised, {
      operatorId: this.operatorService.operatorId,
      userId: userId,
    });
    this.analyticsService.signIn(userId, this.operatorService.operatorId);

    const job = this.jobService
      .create(() => {
        return this.profileService.initialise(userId);
      })
      .showNoInternetRetry(Job.InfiniteRetry);
    job
      .addErrorHandler()
      .showFatalError()
      .onCancel(() => this.signOut());

    await job.runNoThrow();
  }

  private async withFirebaseGuard<T>(operation: () => Promise<T>, context: string): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.analyticsService.handleError(error, context);
      throw error;
    }
  }
}
